<template>
  <div v-if="show" class="container">
    <div class="upper-container">
      <h3 class="title">Notifications</h3>
      <div>
        <button @click="allNotificationsRead()" :disabled="notifications.length <1" class="button is-small all-read">Mark all as read</button>
        <a class="close-icon" @mousedown="close">
          <i class="fal fa-times"></i>
        </a>
      </div>
    </div>
    <ul>
      <infinite-scroll :callback="getNotifications" :distance="50">
        <li class="list-item-notification" v-for="notification in notifications" tabindex="0" :key="notification._id" @click="openNotificationLink(notification.deepLink, notification.id, notification.hasRead)" @keypress.enter.space="openNotificationLink(notification.deepLink, notification.id, notification.hasRead)">
          <div class="images-container">
            <img :src="thumbnail(notification.image)">
            <div class="logo-container">
              <i :class="notificationLogo(notification.jobType)"></i>
            </div>
          </div>
          <div class="text-container">
            <p class="notification-body">{{notification.body}}</p>
            <p class="notification-created">{{convertTime(notification.createdAt)}}</p>
          </div>
          <div class="light-container">
            <i v-if="!notification.hasRead" class="fas fa-circle"></i>
          </div>
        </li>
      </infinite-scroll>
      <loader :loading="loading" />
      <div class="no-notifications has-text-grey-light subtitle has-text-centered">
        {{$t('projectPage.notification.text1')}} {{text}}. {{$t('projectPage.notification.text2')}} {{text}} {{$t('projectPage.notification.text3')}}
      </div>
    </ul>
  </div>
</template>
<script>
import auth from '@/mixins/auth'
import moment from 'moment'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import users from '@/api/users'
import imageProxy from '@/libs/image-proxy'
import i18n from '@/i18n'
import Loader from '../Loader/loader'

export default {
  components: {
    Loader,
    InfiniteScroll
  },
  mixins: [auth],
  data: function () {
    return {
      notifications: [],
      show: false,
      limit: 12,
      offset: 0,
      total: 0,
      hasMore: true,
      loading: false,
      error: ''
    }
  },
  computed: {
    text: function () {
      if (this.$isStories) {
        return i18n.t('projectPage.notification.stories')
      } else {
        return i18n.t('projectPage.notification.projects')
      }
    }
  },
  methods: {
    close: function () {
      this.reset()
      this.show = false
    },
    getNotifications: function () {
      if (this.loading || !this.hasMore || !this.currentUser.userName) {
        return
      }
      const notificationsConfig = { username: this.currentUser.userName, limit: this.limit, offset: this.offset }
      this.loading = true
      this.$store.dispatch('getNotifications', notificationsConfig).then((response) => {
        this.notifications = this.notifications.concat(response.notifications)
        this.total = response.total
        this.hasMore = response.hasMore
        this.loading = false
        this.offset += this.limit
      }, (error) => {
        this.error = error.message
      })
    },
    notificationRead: function (notificationId) {
      const notificationReadConfig = { hasRead: true, objectID: notificationId, userName: this.currentUser.userName }
      users.updateNotifications(notificationReadConfig).then(() => {
        this.$store.dispatch('decrementNotificationCount', 1)
      }, (error) => {
        this.error = error.message
      })
    },
    allNotificationsRead: function () {
      const allNotificationReadConfig = { userName: this.currentUser.userName }
      users.updateAllNotifications(allNotificationReadConfig).then(() => {
        this.$store.dispatch('resetNotificationCount')
        this.reset()
        this.getNotifications()
      }, (error) => {
        this.error = error.message
      })
    },
    openNotificationLink: function (link, notificationId, hasRead) {
      this.close()
      if (!hasRead) {
        this.notificationRead(notificationId)
      }
      this.$router.push(link)
    },
    convertTime: function (date) {
      const newdate = moment.utc(date).local().format()
      return moment(newdate).fromNow()
    },
    reset: function () {
      this.notifications = []
      this.offset = 0
      this.total = 0
      this.hasMore = true
      this.loading = false
    },
    thumbnail: function (projectImage) {
      if (projectImage) {
        return imageProxy.proxy(projectImage, { width: 75, height: 70 })
      } else {
        // Default to image not found
        return imageProxy.proxy('https://firebasestorage.googleapis.com/v0/b/project-leo-mvp.appspot.com/' +
            'o/avatars%2Favatarplaceholder-23edc250-0511-11ec-a888-45891beb194a?' +
            'alt=media&token=721cb3e0-e289-443c-a801-a1b220392b8e', { width: 75, height: 70 })
      }
    },
    notificationLogo: function (projectType) {
      switch (projectType) {
        case 'ProjectAwardNotification':
          return 'fas fa-award'
        case 'CommentNotification':
          return 'fal fa-comment'
        case 'PostNotification':
          return 'fas fa-file-alt'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~styles/utils.scss';
.container {
  background-color: var(--background-secondary);
  min-width: 31rem;
  max-width: 33rem;
  position: fixed;
  top: 3.25rem;
  right: 0;
  z-index: 50;
  box-shadow: 0 2px 2px 0px;
  @include mobile {
    min-width: 100%;
  }
}
.title {
  font-size: 0.9rem;
  color: $primary;
  border-bottom: solid 2px $primary;
  width: 100%;
  max-width: max-content;
  margin: 0;
}

.upper-container {
  display: flex;
  justify-content: space-between;
  padding-right: 1rem;
  padding-left: 2rem;
  padding-top: 1.25rem;
  h3 {
    padding-bottom: 0.25rem;
    margin-bottom: 0.75rem;
  }
}

.close-icon {
  color: black;
}

ul {
  overflow: auto;
  overflow-x: hidden;
  max-height: 85vh;
  @include mobile {
    max-width: 100%;
  }
}

li {
  padding-left: 2rem;
  padding-right: 0.5rem;
  min-height: 3rem;
  max-height: 7rem;
  display: flex;
  cursor: pointer;
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
  justify-content: space-between;
  .images-container {
    margin-right: 1rem;
    width: 75px;
    height: 79px;
    position: relative;
    img {
      width: 75px;
      height: 79px;
      border-radius: 50%;
      max-width: inherit;
    }
  }
}

li:hover {
  background-color: var(--background-fifth);
}
.text-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
}
.notification-body {
  font-size: 0.9rem;
}
.notification-created {
  font-size: 0.7rem;
  color: #7a7a7a;
}

.light-container {
  display: flex;
  align-items: center;
  padding-left: 1rem;
  padding-right: 0.25rem;
  i {
    font-size: 0.7rem;
    color: $primary;
  }
}

.no-notifications {
  margin-top: 1rem;
  padding: 2rem;
  border-top: 0.25px solid darkgray;
}

.loader-container {
  display: flex;
}

.logo-container {
  border-radius: 50%;
  background-color: $secondary;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -4px;
  position: absolute;
  right: -3px;
  i {
    font-weight: bold;
    font-size: 1rem;
    color: white;
  }
}

.all-read {
  margin-right: 0.75rem;
  padding: 0.25rem;
  font-size: 0.65rem;
}

</style>
